import { useCallback, useEffect, useRef, useState } from 'react';
import { usePostFileMutation } from '../../../../../redux/api/fymAPI';
import { PromoteMediaDraft } from '../../draftStore';
import {
  mediaDraftOfUpload,
  pickerUploadKind,
  PickerUploadKind,
  pickerUploadRefusal,
  PickerUploadRefusal,
  prepareUploadImage,
} from './pickerUpload';
import { usePickerVideoSender } from './usePickerVideoSender';

export type PickerUploadState =
  | { status: 'idle' }
  /**
   * The file goes up. `progress` is the percent of the bytes of a video (null for an image, which
   * goes up in one request). `checking` is true while the server checks and measures the video.
   */
  | { status: 'uploading'; fileName: string; kind: PickerUploadKind; progress: number | null; checking: boolean }
  | { status: 'refused'; fileName: string; kind: PickerUploadKind; reason: PickerUploadRefusal }
  | { status: 'failed'; fileName: string; kind: PickerUploadKind; error: unknown };

/** The request on the way, so a cancel can stop it. */
interface RunningUpload {
  abort: () => void;
}

/**
 * "Upload from your device" of the media picker.
 *
 * An image: the browser makes a JPEG of it (`prepareUploadImage`), the JPEG goes up through
 * `POST /accounts/:accountId/files` (the RTK mutation, so the session refresh applies), then
 * becomes the attached media.
 *
 * A video (MP4 or MOV, at most 1 GB): it goes straight to S3 in parts (`usePickerVideoSender`),
 * with the real byte progress. A part that fails goes again up to 3 times; after that the upload
 * fails, and `retry` starts it again. `cancel` stops the parts and aborts the upload on the server.
 *
 * Only the newest request may attach. Each upload, `cancel`, `reset` (the picker attached another
 * file) and the unmount (Cancel or close: the picker body lives only while the dialog is open) give
 * a new request number and stop the running request. A result that comes back with an old number
 * is dropped, so a slow upload never replaces the media of a later choice.
 */
export function usePickerUpload({
  accountId,
  onUploaded,
}: {
  accountId: number;
  onUploaded: (media: PromoteMediaDraft) => void;
}): {
  state: PickerUploadState;
  upload: (file: File) => void;
  retry: () => void;
  cancel: () => void;
  reset: () => void;
} {
  const [postFile] = usePostFileMutation();
  const sendVideo = usePickerVideoSender(accountId);
  const [state, setState] = useState<PickerUploadState>({ status: 'idle' });
  const requestNumber = useRef(0);
  const running = useRef<RunningUpload | null>(null);
  // The last picked file, for "Try again".
  const lastFile = useRef<File | null>(null);

  /** Stops the running request and makes every result of it stale. */
  const cancelRunning = useCallback(() => {
    requestNumber.current += 1;
    running.current?.abort();
    running.current = null;
  }, []);

  useEffect(() => cancelRunning, [cancelRunning]);

  const uploadImage = useCallback(
    async (file: File, isCurrent: () => boolean) => {
      const prepared = await prepareUploadImage(file);
      if (!isCurrent()) {
        return;
      }
      if (prepared.status === 'refused') {
        setState({ status: 'refused', fileName: file.name, kind: 'image', reason: prepared.reason });
        return;
      }
      const formData = new FormData();
      formData.append('file', prepared.blob, prepared.fileName);
      const request = postFile({ accountId, formData });
      running.current = request;
      const saved = await request.unwrap();
      if (running.current === request) {
        running.current = null;
      }
      if (!isCurrent()) {
        return;
      }
      setState({ status: 'idle' });
      onUploaded(mediaDraftOfUpload({ fileId: saved.id, url: saved.url, name: file.name, size: prepared.size }));
    },
    [accountId, onUploaded, postFile]
  );

  const uploadVideo = useCallback(
    async (file: File, isCurrent: () => boolean) => {
      const media = await sendVideo({
        file,
        isCurrent,
        follow: (abort) => {
          // A stale upload never takes the stop of the newer one.
          if (isCurrent()) {
            running.current = abort === null ? null : { abort };
          }
        },
        onProgress: (progress) => {
          if (isCurrent()) {
            setState({ status: 'uploading', fileName: file.name, kind: 'video', progress, checking: false });
          }
        },
        onChecking: () => {
          if (isCurrent()) {
            setState({ status: 'uploading', fileName: file.name, kind: 'video', progress: 100, checking: true });
          }
        },
      });
      if (media === null || !isCurrent()) {
        return;
      }
      setState({ status: 'idle' });
      onUploaded(media);
    },
    [onUploaded, sendVideo]
  );

  const upload = useCallback(
    (file: File) => {
      cancelRunning();
      lastFile.current = file;
      const mine = requestNumber.current;
      const isCurrent = () => requestNumber.current === mine;
      const kind = pickerUploadKind(file);
      const refusal = pickerUploadRefusal(file);
      if (refusal !== null) {
        setState({ status: 'refused', fileName: file.name, kind, reason: refusal });
        return;
      }
      const progress = kind === 'video' ? 0 : null;
      setState({ status: 'uploading', fileName: file.name, kind, progress, checking: false });
      (kind === 'video' ? uploadVideo(file, isCurrent) : uploadImage(file, isCurrent)).catch((error: unknown) => {
        if (!isCurrent()) {
          // A cancel aborts the request: that is not a failure the user should see.
          return;
        }
        console.error(error);
        setState({ status: 'failed', fileName: file.name, kind, error });
      });
    },
    [cancelRunning, uploadImage, uploadVideo]
  );

  const retry = useCallback(() => {
    if (lastFile.current !== null) {
      upload(lastFile.current);
    }
  }, [upload]);

  const reset = useCallback(() => {
    cancelRunning();
    setState({ status: 'idle' });
  }, [cancelRunning]);

  return { state, upload, retry, cancel: reset, reset };
}
